import { Tooltip } from 'components';
import { SHARE_POPUP } from 'constants/popups';
import { HighlightText } from 'pages/PersonalChat/ChatUserList';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { initialFiltersState } from 'sidebars/CreateEventOverall/utils';
import { ChatIcon, FileIcon } from 'static';
import { openModal } from 'store/actionCreators/modal';
import {
  getCurrentCommunity,
  getCurrentUserInCommunity,
} from 'store/selectors/currentCommunity';
import { getEvent, getOverallEvent } from 'store/selectors/events';
import { modalSelector } from 'store/selectors/modal';
import { EventDetailsTab, EventToRender, ModalTypes } from 'types';
import { isAdminOrOwner } from 'utils/roles';

import EventShareOptionsTooltip from '../../EventShareOptionsTooltip';
import EventDeleteConfirmPopups from './EventDeleteConfirmPopups';
import {
  EventTitle,
  StyledCardBottomIconContainer,
  StyledDocumentIcon,
  StyledEventTypeLabel,
  StyledHeadContainer,
  StyledHeadContent,
  StyledHeadFooter,
  StyledHeadFooterLeft,
  StyledHeadText,
  StyledHighlightTop,
  StyledIconHitArea,
  StyledIconTooltipAnchor,
  StyledInfoIcon,
  StyledOptionalMessageIcon,
  StyledSelectionHighlight,
  StyledShareBlock,
  StyledTitle,
} from './styled';

interface HeadProps {
  style: { [key: string]: string | number };
  zoom: number;
  statisticsTab: boolean;
  headData: EventToRender;
  divide?: 'left' | 'right';
  filterValue: string;
}

const Head = memo<HeadProps>(
  ({ style, headData, zoom, statisticsTab, divide, filterValue }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { data, isOpen } = useSelector(modalSelector);
    const community = useSelector(getCurrentCommunity);
    const currentUser = useSelector(getCurrentUserInCommunity);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const {
      color,
      title,
      dateString,
      time,
      width,
      id,
      date,
      eventType,
      description,
      onExportPDFClick,
      isPlusIcon,
      optionalMessage,
      documentsCount,
    } = headData || {};

    const groupEvent = useSelector((state) => getEvent(state, id));
    const overallEvent = useSelector((state) => getOverallEvent(state, id));
    const event = groupEvent || overallEvent;

    const isPast =
      date && new Date(date).getTime() < new Date().setHours(0, 0, 0, 0);

    const isOverallEvent = !!event?.communityId && !event?.groupId;
    const canManageEvent =
      !!event &&
      !!currentUser &&
      !(isOverallEvent && !isAdminOrOwner(currentUser));

    const handleEditClick = () => {
      if (!event) {
        return;
      }

      dispatch(
        openModal({
          type: isOverallEvent
            ? ModalTypes.CREATE_EVENT_OVERALL
            : ModalTypes.CREATE_EVENT,
          placement: 'right',
          data: isOverallEvent
            ? {
                overallScheduler: community?.overallScheduler,
                event,
                date,
                filterFrom: initialFiltersState().from,
                filterTo: initialFiltersState().to,
              }
            : {
                groupId: event.groupId,
                event,
                date,
                filterFrom: initialFiltersState().from,
                filterTo: initialFiltersState().to,
              },
        }),
      );
    };

    return (
      <>
        <div
          style={style}
          data-type="header"
          data-id={id}
          data-clickable="true"
          data-date={date}
          className="grid-clickable-item"
        >
          {isOpen && data?.['eventId']?.toString() === id?.toString() && (
            <StyledSelectionHighlight zoom={zoom} />
          )}
          {isOpen && data?.['eventId']?.toString() === id?.toString() && (
            <StyledHighlightTop zoom={zoom} />
          )}
          <StyledHeadContainer
            divide={divide}
            selected={
              isOpen && data?.['eventId']?.toString() === id?.toString()
            }
          >
            <StyledHeadContent
              flex={!statisticsTab}
              centered={!dateString && !time}
              color={color}
              width={width}
              zoom={zoom}
              statisticsTab={statisticsTab}
              isPast={isPast}
              selected={
                isOpen && data?.['eventId']?.toString() === id?.toString()
              }
            >
              {!statisticsTab && !isPlusIcon && (
                <Tooltip
                  isVisible
                  visibleAfterWidth={93 * zoom}
                  zoom={zoom}
                  text={eventType}
                  tooltipStyle={{
                    fontSize: 12 * zoom,
                    maxWidth: 200 * zoom,
                    display: 'block',
                    whiteSpace: 'pre-wrap',
                    lineBreak: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  <StyledEventTypeLabel zoom={zoom}>
                    <span>{eventType}</span>
                  </StyledEventTypeLabel>
                </Tooltip>
              )}
              <Tooltip
                isVisible
                zoom={zoom}
                text={
                  id === 'noDataFound'
                    ? t('common.noResultsFound')
                    : isPlusIcon
                    ? t('event.createEvent')
                    : title
                }
                offset={(statisticsTab ? 4 : 26) * zoom}
                tooltipStyle={{
                  fontSize: 12 * zoom,
                  maxWidth: 200 * zoom,
                  display: 'block',
                  whiteSpace: 'pre-wrap',
                  lineBreak: 'normal',
                }}
              >
                <StyledTitle isStatisticsTab={statisticsTab} zoom={zoom}>
                  {isPlusIcon ? (
                    <StyledTitle.PlusIconText
                      zoom={zoom}
                      isNoData={id === 'noDataFound'}
                      dangerouslySetInnerHTML={
                        isPlusIcon ? { __html: title.toString() } : undefined
                      }
                    />
                  ) : (
                    <EventTitle>
                      <HighlightText
                        searchText={filterValue}
                        text={title as string}
                      />
                    </EventTitle>
                  )}
                  {isPlusIcon && description && (
                    <StyledTitle.Description zoom={zoom}>
                      {description}
                    </StyledTitle.Description>
                  )}
                </StyledTitle>
              </Tooltip>
              <StyledHeadFooter>
                <StyledHeadFooterLeft>
                  {dateString && (
                    <StyledHeadText zoom={zoom}>
                      {t(
                        `dayOfWeek.day${new Date(
                          dateString.slice(3, 5) +
                            '/' +
                            dateString.slice(0, 2) +
                            '/' +
                            dateString.slice(6),
                        ).getDay()}`,
                      )}
                      {', '}
                      {dateString}
                    </StyledHeadText>
                  )}
                  {<StyledHeadText zoom={zoom}>{time}&nbsp;</StyledHeadText>}
                </StyledHeadFooterLeft>

                <StyledCardBottomIconContainer>
                  {optionalMessage?.length > 0 && (
                    <Tooltip
                      isVisible
                      zoom={zoom}
                      opacity="75"
                      text={t('event.optionalMessage')}
                      offset={11 * zoom}
                      position="right-center"
                      tooltipStyle={{
                        fontSize: 12 * zoom,
                        maxWidth: 200 * zoom,
                        display: 'block',
                        whiteSpace: 'pre-wrap',
                        lineBreak: 'normal',
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <div
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            left: '-8px',
                            right: '-8px',
                            bottom: '-8px',
                            zIndex: 1,
                          }}
                        />
                        <StyledOptionalMessageIcon zoom={zoom}>
                          <ChatIcon />
                        </StyledOptionalMessageIcon>
                      </div>
                    </Tooltip>
                  )}

                  {(documentsCount ?? 0) > 0 && (
                    <Tooltip
                      isVisible
                      zoom={zoom}
                      opacity="75"
                      text={t('common.documentation')}
                      offset={11 * zoom}
                      position="right-center"
                      tooltipStyle={{
                        fontSize: 12 * zoom,
                        maxWidth: 200 * zoom,
                        display: 'block',
                        whiteSpace: 'pre-wrap',
                        lineBreak: 'normal',
                      }}
                    >
                      <StyledIconTooltipAnchor
                        data-clickable="true"
                        data-type="header"
                        data-id={id}
                        data-date={date}
                        data-initial-tab={EventDetailsTab.DOCUMENTATION}
                      >
                        <StyledIconHitArea />
                        <StyledDocumentIcon zoom={zoom}>
                          <FileIcon />
                        </StyledDocumentIcon>
                      </StyledIconTooltipAnchor>
                    </Tooltip>
                  )}

                  {!!description && !isPlusIcon && (
                    <Tooltip
                      isVisible
                      zoom={zoom}
                      text={description}
                      offset={11 * zoom}
                      opacity="0.9"
                      position="right-center"
                      tooltipStyle={{
                        fontSize: 12 * zoom,
                        maxWidth: 200 * zoom,
                        display: 'block',
                        whiteSpace: 'pre-wrap',
                        lineBreak: 'normal',
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <div
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            left: '-8px',
                            right: '-8px',
                            bottom: '-8px',
                            zIndex: 1,
                          }}
                        />
                        <StyledInfoIcon zoom={zoom}>
                          <span>i</span>
                        </StyledInfoIcon>
                      </div>
                    </Tooltip>
                  )}
                </StyledCardBottomIconContainer>
              </StyledHeadFooter>
              {!statisticsTab && !isPlusIcon && (
                <StyledShareBlock zoom={zoom}>
                  <EventShareOptionsTooltip
                    zoom={zoom}
                    onShareClick={() =>
                      dispatch(
                        openModal({
                          type: SHARE_POPUP,
                          data: {
                            eventId: id,
                          },
                        }),
                      )
                    }
                    onExportPDFClick={onExportPDFClick}
                    onEditClick={canManageEvent ? handleEditClick : undefined}
                    onDeleteClick={
                      canManageEvent
                        ? () => setIsDeleteConfirmOpen(true)
                        : undefined
                    }
                  />
                </StyledShareBlock>
              )}
            </StyledHeadContent>
          </StyledHeadContainer>
        </div>
        {canManageEvent && event && (
          <EventDeleteConfirmPopups
            event={event}
            isOpen={isDeleteConfirmOpen}
            onClose={() => setIsDeleteConfirmOpen(false)}
          />
        )}
      </>
    );
  },
);

Head.displayName = 'Head';

export default Head;
